import { useState, useEffect, useCallback } from "react";
import { X, Check, ChevronsUpDown } from "lucide-react";
import dayjs from "dayjs";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { DatePickerV2 } from "@/components/fields/date-picker";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";

import { cn } from "@/lib/utils";
import { isDateTimeWithClockField } from "@/lib/pps-date-time";

// Data types enum matching backend
export enum CellDataType {
  FOREIGN_KEY = 0,
  TEXT = 1,
  INT = 2,
  FRACTION = 3,
  DATETIME = 4,
  BOOLEAN = 5,
  MULTISELECT = 6
}

interface EditableCellProps {
  isEditing: boolean;
  value: any;
  dataType: CellDataType;
  attribute: string;
  referenceTable?: string;
  nestedColumnName?: string;
  onSave: (data: Record<string, any>) => void;
  onCancelEdit: () => void;
  foreignKeyOptions?: { label: string; value: string | number }[];
  onForeignKeySearch?: (search: string) => void;
  disabled?: boolean;
  multiselectOptions?: { label: string; value: string | number }[];
}

export function EditableCell({
  isEditing,
  value,
  dataType,
  attribute,
  onSave,
  onCancelEdit,
  foreignKeyOptions = [],
  onForeignKeySearch,
  disabled = false,
  multiselectOptions = []
}: EditableCellProps) {
  const [editValue, setEditValue] = useState<any>(
    dataType === CellDataType.DATETIME
      ? value
        ? new Date(value)
        : undefined
      : (value ?? "")
  );
  const [error, setError] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const isDateTimeWithClock = isDateTimeWithClockField(attribute);

  useEffect(() => {
    if (isEditing) {
      setEditValue(
        dataType === CellDataType.DATETIME
          ? value
            ? new Date(value)
            : undefined
          : (value ?? "")
      );
      setError("");
    }
  }, [isEditing, value, dataType]);

  useEffect(() => {
    if (
      popoverOpen &&
      dataType === CellDataType.FOREIGN_KEY &&
      onForeignKeySearch
    ) {
      onForeignKeySearch("");
    }
  }, [popoverOpen, dataType, onForeignKeySearch]);

  const validate = useCallback(() => {
    if (dataType === CellDataType.INT || dataType === CellDataType.FRACTION) {
      const num = Number.parseFloat(String(editValue));
      if (isNaN(num)) {
        setError("Please enter a valid number");
        return false;
      }
    }
    setError("");
    return true;
  }, [editValue, dataType]);

  const handleSave = useCallback(() => {
    if (validate()) {
      let saveValue = editValue;

      // Format value based on type
      if (dataType === CellDataType.DATETIME && editValue instanceof Date) {
        saveValue = dayjs(editValue).format(
          isDateTimeWithClock ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD"
        );
      } else if (dataType === CellDataType.INT) {
        saveValue = parseInt(String(editValue), 10);
      } else if (dataType === CellDataType.FRACTION) {
        saveValue = parseFloat(String(editValue));
      }

      onSave({ [attribute]: saveValue });
    }
  }, [validate, editValue, dataType, isDateTimeWithClock, attribute, onSave]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      onCancelEdit();
    }
  };

  const handleBlur = () => {
    if (isEditing && !error) {
      handleSave();
    }
  };

  const renderEditableInput = () => {
    switch (dataType) {
      case CellDataType.TEXT:
        return (
          <Input
            type="text"
            className="aero:w-full aero:h-8"
            value={editValue as string}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={disabled}
            autoFocus
          />
        );

      case CellDataType.INT:
        return (
          <Input
            type="number"
            className="aero:w-full aero:h-8"
            value={editValue as number}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={disabled}
            autoFocus
          />
        );

      case CellDataType.FRACTION:
        return (
          <Input
            type="number"
            step="0.01"
            className="aero:w-full aero:h-8"
            value={editValue as number}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={disabled}
            autoFocus
          />
        );

      case CellDataType.FOREIGN_KEY: {
        return (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                intent="primary"
                design="outline"
                size="sm"
                className={cn(
                  "aero:w-full aero:justify-between aero:text-left aero:font-normal aero:h-8 aero:border-neutral-300",
                  !editValue && "aero:text-neutral-400"
                )}
                disabled={disabled}
              >
                <span className="aero:truncate">
                  {editValue
                    ? foreignKeyOptions.find((opt) => opt.value === editValue)
                        ?.label || "Select..."
                    : "Select..."}
                </span>
                <ChevronsUpDown className="aero:ml-2 aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="aero:w-[200px] aero:p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Search..."
                  onValueChange={(search) => onForeignKeySearch?.(search)}
                />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  <CommandGroup>
                    {foreignKeyOptions.map((option) => (
                      <CommandItem
                        key={String(option.value)}
                        value={option.label}
                        onSelect={() => {
                          setEditValue(option.value);
                          setPopoverOpen(false);
                          onSave({ [attribute]: option.value });
                        }}
                      >
                        <Check
                          className={cn(
                            "aero:mr-2 aero:h-4 aero:w-4",
                            editValue === option.value
                              ? "aero:opacity-100"
                              : "aero:opacity-0"
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        );
      }

      case CellDataType.DATETIME:
        return (
          <DatePickerV2
            value={
              editValue instanceof Date
                ? editValue
                : editValue
                  ? new Date(editValue)
                  : null
            }
            onChange={(date) => {
              setEditValue(date);
              if (!date) {
                onSave({ [attribute]: "" });
                return;
              }

              onSave({
                [attribute]: dayjs(date).format(
                  isDateTimeWithClock ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD"
                )
              });
            }}
            placeholder={
              isDateTimeWithClock ? "Pick date and time" : "Pick a date"
            }
            showTime={isDateTimeWithClock}
            disabled={disabled}
            className="aero:w-full"
          />
        );

      case CellDataType.BOOLEAN:
        return (
          <div className="aero:flex aero:items-center aero:gap-2">
            <Switch
              checked={editValue === true || editValue === "true"}
              onCheckedChange={(checked) => {
                setEditValue(checked);
                onSave({ [attribute]: checked });
              }}
              disabled={disabled}
            />
            <span className="aero:text-sm aero:text-neutral-600">
              {editValue === true || editValue === "true" ? "Yes" : "No"}
            </span>
          </div>
        );

      case CellDataType.MULTISELECT:
        return (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                intent="neutral"
                design="outline"
                size="sm"
                className={cn(
                  "aero:w-full aero:justify-between aero:text-left aero:font-normal aero:border-neutral-300",
                  !editValue && "aero:text-neutral-400"
                )}
                disabled={disabled}
              >
                {editValue
                  ? multiselectOptions.find((opt) => opt.value === editValue)
                      ?.label || "Select..."
                  : "Select..."}
                <ChevronsUpDown className="aero:ml-2 aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="aero:w-[300px] aero:p-0" align="start">
              <Command>
                <CommandInput placeholder="Search..." />
                <CommandList>
                  <CommandEmpty>No option found.</CommandEmpty>
                  <CommandGroup>
                    {multiselectOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={() => {
                          setEditValue(option.value);
                          onSave({ [attribute]: option.value });
                          setPopoverOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "aero:mr-2 aero:h-4 aero:w-4",
                            editValue === option.value
                              ? "aero:opacity-100"
                              : "aero:opacity-0"
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        );

      default:
        return (
          <Input
            type="text"
            className="aero:w-full aero:h-8"
            value={editValue as string}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={disabled}
            autoFocus
          />
        );
    }
  };

  if (!isEditing) {
    return null;
  }

  return (
    <div
      className={cn(
        "aero:w-full aero:min-h-[32px] aero:px-2 aero:py-1 aero:rounded",
        "aero:bg-brand-primary-50 aero:ring-2 aero:ring-brand-primary-500",
        "aero:relative"
      )}
    >
      <button
        type="button"
        className="aero:absolute aero:top-1 aero:right-1 aero:p-0.5 aero:rounded-full aero:bg-neutral-300 aero:hover:bg-neutral-400"
        onClick={onCancelEdit}
      >
        <X className="aero:h-3 aero:w-3" />
      </button>

      <div className="aero:pr-5">{renderEditableInput()}</div>

      {error && (
        <div className="aero:text-xs aero:text-red-500 aero:mt-1">{error}</div>
      )}
    </div>
  );
}
